import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import Keycloak from 'keycloak-js';
import { ConfigInitService } from './config-init.service';
import { LookupService } from './lookup.service';
import { WebStorage } from '../components/auth/storage/web.storage';
import { EncryptedStorageService } from './encrypted-storage.service';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private SERVER_API_URL = environment.serviceUrl;
    private keycloak: Keycloak.KeycloakInstance | null = null;
    private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
    public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
    public loginFailReason: 'ACCOUNT_DEACTIVATED' | null = null;

    constructor(
        private http: HttpClient,
        private router: Router,
        private configService: ConfigInitService,
        private lookupService: LookupService,
        private storage: WebStorage,
        private encryptedStorage: EncryptedStorageService,
    ) {}

    async init(): Promise<void> {
        try {
            const config = await this.configService.getConfig().toPromise();
            if (!config) {
                throw new Error('Config not loaded');
            }

            this.keycloak = new Keycloak({
                url: config['KEYCLOAK_URL'],
                realm: config['KEYCLOAK_REALM'],
                clientId: config['KEYCLOAK_CLIENT_ID'],
            });

            // Always initialize Keycloak first
            const authenticated = await this.keycloak.init({
                onLoad: 'check-sso',
                silentCheckSsoRedirectUri:
                    config['KEYCLOAK_REALM'] === 'wowcare_preprod'
                        ? undefined
                        : window.location.origin +
                          environment.path +
                          '/assets/layout/silent-check-sso.html',
                checkLoginIframe: false, // Disable iframe to prevent conflicts
                checkLoginIframeInterval: 1000,
                enableLogging: false, // Reduce logging in production
            });

            if (authenticated) {
                // Keycloak authenticated us, store tokens and load profile
                await this.storeTokens();
                this.clearKioskAccess();
                const profileLoaded1 = await this.loadUserProfile();
                if (!profileLoaded1) {
                    await this.clearSessionWithoutRedirect();
                    return;
                }
                await this.checkAndSetKioskAccess();
                this.isAuthenticatedSubject.next(true);
            } else {
                // Check if we have stored tokens from previous session
                try {
                    const existingToken =
                        await this.encryptedStorage.getEncryptedItem(
                            'access_token',
                        );
                    const existingRefreshToken =
                        await this.encryptedStorage.getEncryptedItem(
                            'refresh_token',
                        );

                    if (existingToken && existingRefreshToken) {
                        // Try to restore session with existing tokens
                        this.keycloak.token = existingToken;
                        this.keycloak.refreshToken = existingRefreshToken;
                        this.keycloak.idToken =
                            (await this.encryptedStorage.getEncryptedItem(
                                'id_token',
                            )) || undefined;

                        // Try to refresh token to verify it's still valid
                        try {
                            const refreshed =
                                await this.keycloak.updateToken(30);
                            if (refreshed) {
                                await this.storeTokens();
                                this.clearKioskAccess();
                                const profileLoaded2 = await this.loadUserProfile();
                                if (!profileLoaded2) {
                                    await this.clearSessionWithoutRedirect();
                                    return;
                                }
                                await this.checkAndSetKioskAccess();
                                this.isAuthenticatedSubject.next(true);
                                return;
                            } else {
                                // Token is still valid, no refresh needed
                                this.clearKioskAccess();
                                const profileLoaded3 = await this.loadUserProfile();
                                if (!profileLoaded3) {
                                    await this.clearSessionWithoutRedirect();
                                    return;
                                }
                                await this.checkAndSetKioskAccess();
                                this.isAuthenticatedSubject.next(true);
                                return;
                            }
                        } catch (error) {
                            // Tokens expired, clear them
                            this.clearTokens();
                        }
                    }
                } catch (error) {
                    // If decryption fails, clear tokens and continue with normal flow
                    this.clearTokens();
                }
            }

            // Set authenticated state - update if we successfully restored session
            let finalAuthenticated = authenticated;

            if (!authenticated) {
                // Check if we successfully restored and refreshed tokens
                const hasValidToken = await this.getToken();
                finalAuthenticated = !!hasValidToken;

                if (finalAuthenticated) {
                    this.clearKioskAccess();
                    const profileLoaded4 = await this.loadUserProfile();
                    if (profileLoaded4) {
                        await this.checkAndSetKioskAccess();
                    } else {
                        await this.clearSessionWithoutRedirect();
                        finalAuthenticated = false;
                    }
                }
            }

            this.isAuthenticatedSubject.next(finalAuthenticated);
        } catch (error) {
            console.error('Keycloak init error:', error);
            this.isAuthenticatedSubject.next(false);
        }
    }

    private async storeTokens(): Promise<void> {
        if (this.keycloak) {
            const token = this.keycloak.token;
            const refreshToken = this.keycloak.refreshToken;
            const idToken = this.keycloak.idToken;

            if (token)
                await this.encryptedStorage.setEncryptedItem(
                    'access_token',
                    token,
                );
            if (refreshToken)
                await this.encryptedStorage.setEncryptedItem(
                    'refresh_token',
                    refreshToken,
                );
            if (idToken)
                await this.encryptedStorage.setEncryptedItem(
                    'id_token',
                    idToken,
                );
        }
    }

    private clearTokens(): void {
        this.encryptedStorage.removeItem('access_token');
        this.encryptedStorage.removeItem('refresh_token');
        this.encryptedStorage.removeItem('id_token');
        this.encryptedStorage.removeItem('code_verifier');
    }

    async login(username: string, password: string): Promise<boolean> {
        try {
            const config = await this.configService.getConfig().toPromise();
            if (!config) return false;

            // Generate PKCE code verifier and challenge for additional security
            const codeVerifier = this.generateCodeVerifier();
            const codeChallenge =
                await this.generateCodeChallenge(codeVerifier);

            const tokenUrl = `${config['KEYCLOAK_URL']}/realms/${config['KEYCLOAK_REALM']}/protocol/openid-connect/token`;

            const body = new URLSearchParams();
            body.set('grant_type', 'password');
            body.set('client_id', config['KEYCLOAK_CLIENT_ID']);
            body.set('username', username);
            body.set('password', password);
            body.set(
                'scope',
                'openid address profile microprofile-jwt offline_access phone email',
            );
            body.set('code_challenge', codeChallenge);
            body.set('code_challenge_method', 'S256');

            const response = await this.http
                .post<any>(tokenUrl, body.toString(), {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/x-www-form-urlencoded',
                    }),
                })
                .toPromise();

            if (response && response.access_token) {
                await this.encryptedStorage.setEncryptedItem(
                    'access_token',
                    response.access_token,
                );
                await this.encryptedStorage.setEncryptedItem(
                    'refresh_token',
                    response.refresh_token || '',
                );
                await this.encryptedStorage.setEncryptedItem(
                    'id_token',
                    response.id_token || '',
                );
                this.encryptedStorage.setItem('code_verifier', codeVerifier); // Store for potential future use (non-sensitive)

                // Clear any stale kiosk flag from a previous session before loading profile
                this.clearKioskAccess();

                // Load user profile and check kiosk designation
                const profileLoaded = await this.loadUserProfile();
                if (!profileLoaded) {
                    await this.clearSessionWithoutRedirect();
                    if (this.loginFailReason === 'ACCOUNT_DEACTIVATED') {
                        this.router.navigate(['/notfound']);
                    }
                    return false;
                }
                await this.checkAndSetKioskAccess();

                this.isAuthenticatedSubject.next(true);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Login error:', error);
            return false;
        }
    }

    private generateCodeVerifier(): string {
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        return this.base64URLEncode(array);
    }

    private async generateCodeChallenge(verifier: string): Promise<string> {
        const encoder = new TextEncoder();
        const data = encoder.encode(verifier);
        const digest = await crypto.subtle.digest('SHA-256', data);
        return this.base64URLEncode(new Uint8Array(digest));
    }

    private base64URLEncode(array: Uint8Array): string {
        const base64 = btoa(String.fromCharCode.apply(null, Array.from(array)));
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }

    async logout(): Promise<void> {
        try {
            // Clear custom storage
            localStorage.removeItem('logQueryParam');
            localStorage.removeItem('activeStudent');
            this.clearKioskAccess(); // Clear kiosk access on logout
            await this.storage.logout();

            // Clear tokens and Keycloak instance
            this.clearTokens();
            if (this.keycloak) {
                // Clear Keycloak tokens
                this.keycloak.clearToken();
                this.keycloak = null;
            }

            this.isAuthenticatedSubject.next(false);

            // Navigate to login page instead of Keycloak redirect
            this.router.navigate(['/login']);
        } catch (error) {
            console.error('Logout error:', error);
        }
    }

    async clearSessionWithoutRedirect(): Promise<void> {
        try {
            localStorage.removeItem('logQueryParam');
            localStorage.removeItem('activeStudent');
            this.clearKioskAccess();
            await this.storage.logout();
            this.clearTokens();
            if (this.keycloak) {
                this.keycloak.clearToken();
                this.keycloak = null;
            }
            this.isAuthenticatedSubject.next(false);
        } catch (error) {
            console.error('Clear session error:', error);
        }
    }

    async forgotPassword(email: string): Promise<boolean> {
        try {
            const response = await this.http
                .post(`${this.SERVER_API_URL}/auth/request-reset`, { email })
                .toPromise();
            return true; // Success if no error thrown
        } catch (error) {
            console.error('Forgot password error:', error);
            throw error;
        }
    }

    async resetPassword(token: string, newPassword: string): Promise<boolean> {
        try {
            const response = await this.http
                .post(`${this.SERVER_API_URL}/auth/reset-password`, {
                    token,
                    newPassword,
                })
                .toPromise();
            return true; // Success if no error thrown
        } catch (error) {
            console.error('Reset password error:', error);
            throw error;
        }
    }

    async checkLogin(username: string, password: string): Promise<any> {
        try {
            const response = await this.http
                .post(`${this.SERVER_API_URL}/auth/check-login`, {
                    username,
                    password,
                })
                .toPromise();
            return response;
        } catch (error) {
            console.error('Check login error:', error);
            throw error;
        }
    }

    async changePassword(
        username: string,
        currentPassword: string,
        newPassword: string,
    ): Promise<any> {
        try {
            const response = await this.http
                .post(`${this.SERVER_API_URL}/auth/change-password`, {
                    username,
                    currentPassword,
                    newPassword,
                })
                .toPromise();
            return response;
        } catch (error) {
            console.error('Change password error:', error);
            throw error;
        }
    }

    private async loadUserProfile(): Promise<boolean> {
        this.loginFailReason = null;
        try {
            const logValue = localStorage.getItem('logQueryParam');
            const refTable =
                logValue === 'Staff' || logValue === null
                    ? 'Staff'
                    : 'Guardian';

            const account = await this.lookupService
                .queryAccount({ refTable: refTable })
                .toPromise();

            if (account && account.body) {
                const accountBody = account.body as any;
                if (accountBody.activated === false) {
                    if (refTable === 'Staff') {
                        // Staff account is deactivated — attempt Guardian fallback
                        const guardianAccount = await this.lookupService
                            .queryAccount({ refTable: 'Guardian' })
                            .toPromise();

                        const guardianBody = guardianAccount?.body as any;
                        if (guardianBody?.activated === true) {
                            localStorage.setItem('logQueryParam', 'Guardian');
                            this.storage.login(guardianAccount!.body);
                            return true;
                        }
                    }
                    // Staff with no active Guardian, or Guardian directly deactivated
                    this.loginFailReason = 'ACCOUNT_DEACTIVATED';
                    return false;
                }
                this.storage.login(account.body);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Load user profile error:', error);
            return false;
        }
    }

    async getToken(): Promise<string | null> {
        return await this.encryptedStorage.getEncryptedItem('access_token');
    }

    isAuthenticated(): boolean {
        // Check the subject value first
        const subjectValue = this.isAuthenticatedSubject.value;
        if (subjectValue) {
            return true;
        }

        // As a fallback, try to check if we have a token stored
        // This is synchronous check using localStorage directly
        try {
            const stored = localStorage.getItem('access_token');
            if (stored) {
                // We have encrypted data, assume it's valid for now
                // The async init() should have validated this
                return true;
            }
        } catch (error) {
            // Ignore localStorage errors
        }

        return false;
    }

    async refreshToken(): Promise<boolean> {
        try {
            const refreshToken =
                await this.encryptedStorage.getEncryptedItem('refresh_token');
            if (!refreshToken || !this.keycloak) return false;

            const refreshed = await this.keycloak.updateToken(30);
            if (refreshed) {
                await this.storeTokens();
                return true;
            }
            // If updateToken returns false, token might still be valid
            // Check if we have a valid token that doesn't need refresh
            const currentToken = await this.getToken();
            return !!currentToken;
        } catch (error) {
            return false;
        }
    }

    // Kiosk access management methods
    async checkAndSetKioskAccess(): Promise<void> {
        try {
            const userData = this.storage.getUserData();
            if (userData.refTable === 'Staff') {
                if (userData && userData.refTableId) {
                    const staffResponse = await this.lookupService
                        .getStaffById(userData.refTableId)
                        .toPromise();
                    if (staffResponse && staffResponse.body) {
                        const staff = staffResponse.body;

                        if (!userData.activated) {
                            localStorage.setItem('isKiosk', 'true');
                            return;
                        }

                        const isKiosk = staff.designation === 'Kiosk';
                        localStorage.setItem('isKiosk', isKiosk.toString());
                    }
                }
            } else {
                localStorage.setItem('isKiosk', 'false');
            }
        } catch (error) {
            localStorage.setItem('isKiosk', 'false');
        }
    }

    isKioskUser(): boolean {
        const isKiosk = localStorage.getItem('isKiosk');
        return isKiosk === 'true';
    }

    clearKioskAccess(): void {
        localStorage.removeItem('isKiosk');
    }

    async requestEmailChange(
        userId: any,
        oldEmail: string,
        newEmail: string,
        refTable: string,
    ): Promise<any> {
        try {
            const response = await this.http
                .post(`${this.SERVER_API_URL}/${userId}/email-change`, {
                    oldEmail,
                    newEmail,
                    refTable,
                })
                .toPromise();
            return response;
        } catch (error) {
            console.error('Request email change error:', error);
            throw error;
        }
    }

    async verifyEmailChange(token: string): Promise<any> {
        try {
            const response = await this.http
                .post(
                    `${this.SERVER_API_URL}/email-change/verify?token=${token}`,
                    {},
                )
                .toPromise();
            return response;
        } catch (error) {
            console.error('Verify email change error:', error);
            throw error;
        }
    }
}
